import { useState, useEffect } from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useTranslation from '@/hooks/useTranslation';
import { resolveTranslation } from '@/utils/resolveTranslation';
import { translateCompareSegments } from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';

interface Filter {
  _id: string;
  name: string;
}

interface CompareFiltersModalProps {
  filters: Filter[];
  isOpen: boolean;
  onClose: () => void;
  onCompare: (filterIdA: string, filterIdB: string) => void;
  isLoading: boolean;
  analysisLanguage: string;
}

function CompareFiltersModal({
  filters,
  isOpen,
  onClose,
  onCompare,
  isLoading,
  analysisLanguage,
}: CompareFiltersModalProps) {
  const { lang } = useTranslation();
  const [filterA, setFilterA] = useState('');
  const [filterB, setFilterB] = useState('');

  // Reset selections whenever the dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFilterA('');
      setFilterB('');
    }
  }, [isOpen]);

  const canCompare = filterA !== '' && filterB !== '' && filterA !== filterB && !isLoading;

  function handleCompare() {
    if (canCompare) {
      onCompare(filterA, filterB);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-neutral-900">
            {resolveTranslation(translateCompareSegments, analysisLanguage)}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-neutral-700">
              {lang.get('msg.segmentA')}
            </label>
            <Select value={filterA} onValueChange={setFilterA} disabled={isLoading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={lang.get('msg.selectSegment')} />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                {filters.map((f) => (
                  <SelectItem key={f._id} value={f._id} disabled={f._id === filterB}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-neutral-700">
              {lang.get('msg.segmentB')}
            </label>
            <Select value={filterB} onValueChange={setFilterB} disabled={isLoading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={lang.get('msg.selectSegment')} />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                {filters.map((f) => (
                  <SelectItem key={f._id} value={f._id} disabled={f._id === filterA}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            {lang.get('msg.cancel')}
          </Button>
          <Button type="button" onClick={handleCompare} disabled={!canCompare} className="gap-2">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {lang.get('msg.comparing')}
              </>
            ) : (
              <>
                <ArrowLeftRight className="h-4 w-4" />
                {lang.get('msg.compare')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompareFiltersModal;
